import { SharedThreadPage } from './sharing/Sharing';
import PageNavigation from './navigation/PageNavigation';
import { SupportBoundary, SupportButton, SupportPage } from './support/Support';
import React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import useVoyagerStore from './stores/useVoyagerStore';
import { APPEARANCE_EVENT, DEFAULT_APPEARANCE, appearanceAccountKey, applyStromaAppearance, clearPendingStromaAppearance, getLegacyStromaAppearance, getPendingStromaAppearance, getStromaAppearance, markStromaAppearancePending, normalizeStromaAppearance, setStromaAppearance } from './design/ThemeProvider';
import LadderShell from './shell/LadderShell';
import VerdictPanel from './components/Ladder/VerdictPanel';
import { NumberPopoverHost } from './components/Ladder/NumberPopover';
import useAppShortcuts from './hooks/useAppShortcuts';
import { AuthGate, clerkEnabled, useAccountIdentity } from './auth/clerk';
import ExtensionConnect from './components/Extension/ExtensionConnect';
import { getMe } from './apis';
import { reconcileAppearancePreference } from './design/appearancePreferences';
import AppThemeProvider from './design/AppThemeProvider';
import './App.css';

const DesignGallery = React.lazy(() => import('./design/DesignGallery'));

export function AppearancePreferenceSync() {
  const accountIdentity = useAccountIdentity();
  React.useEffect(() => {
    if (!clerkEnabled()) return undefined;
    let live = true;
    let changedLocally = false;
    applyStromaAppearance(accountIdentity ? getStromaAppearance(accountIdentity) : DEFAULT_APPEARANCE, accountIdentity);
    const onAppearance = (event: Event) => {
      const detail = (event as CustomEvent<{ accountIdentity?: string | null }>).detail;
      if (detail?.accountIdentity === accountIdentity) changedLocally = true;
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === appearanceAccountKey(accountIdentity || '') || event.key === null) changedLocally = true;
    };
    window.addEventListener(APPEARANCE_EVENT, onAppearance);
    window.addEventListener('storage', onStorage);
    void getMe().then((me) => {
      const appearance = normalizeStromaAppearance(me.preferences?.appearance);
      if (!live || changedLocally) return;
      const pending = getPendingStromaAppearance(accountIdentity);
      if (pending && JSON.stringify(pending) !== JSON.stringify(appearance)) {
        setStromaAppearance(pending, accountIdentity);
        void reconcileAppearancePreference(accountIdentity, pending).catch(() => undefined);
        return;
      }
      const legacy = !appearance ? getLegacyStromaAppearance() : null;
      if (legacy) {
        markStromaAppearancePending(accountIdentity, legacy);
        setStromaAppearance(legacy, accountIdentity);
        void reconcileAppearancePreference(accountIdentity, legacy).catch(() => undefined);
        return;
      }
      if (pending && appearance) clearPendingStromaAppearance(accountIdentity, appearance);
      setStromaAppearance(appearance ?? DEFAULT_APPEARANCE, accountIdentity);
    }).catch(() => undefined);
    return () => {
      live = false;
      window.removeEventListener(APPEARANCE_EVENT, onAppearance);
      window.removeEventListener('storage', onStorage);
    };
  }, [accountIdentity]);
  return null;
}

function ProductExperience() {
  const workspaceGeneration = useVoyagerStore(s => s.threadsGeneration);
  const error = useVoyagerStore((s) => s.error);
  const errorDetails = useVoyagerStore((s) => s.errorDetails);
  const isLoading = useVoyagerStore((s) => s.isLoading);
  useAppShortcuts();

  // Threads live on the server (#181). With Clerk on, the persistence
  // namespace handler fetches them once the signed-in user is known; without
  // it there is no such signal, so fetch on mount.
  React.useEffect(() => {
    const threadLink = window.location.pathname.startsWith('/threads/') || /^\/reviews\/[^/]+$/.test(window.location.pathname) || new URLSearchParams(window.location.search).has('thread');
    if (!clerkEnabled() && !threadLink) useVoyagerStore.getState().fetchThreads();
  }, []);

  // The extension's connect page (#195): `/?view=extension` opens the dialog
  // that mints this browser's token; the extension's own content script on
  // this origin takes it from there. Read once and stripped, like the others.
  const [extensionConnect, setExtensionConnect] = React.useState(false);
  React.useEffect(() => {
    const view = new URLSearchParams(window.location.search).get('view');
    if (view !== 'extension') return;
    setExtensionConnect(true);
    const url = new URL(window.location.href);
    url.searchParams.delete('view');
    window.history.replaceState({}, '', url.toString());
  }, []);

  const handleCloseError = () => {
    useVoyagerStore.setState({ error: null, errorDetails: null });
  };

  // Suppress the global snackbar for query failures — the rich diagnostic
  // card in BottomResults is the canonical surface for those.
  const showSnackbar = !!error && !errorDetails;

  return (
    <>
      <NumberPopoverHost>
        {/* the verdict side panel: a walk's "Why this verdict" and a review number's — inside the host, so the numbers in it are clickable too */}
        <VerdictPanel />
        <SupportBoundary>
          {window.location.pathname.startsWith('/shared/threads/') ? <SharedThreadPage key={workspaceGeneration} threadId={window.location.pathname.split('/')[3]} /> : window.location.pathname.startsWith('/support/') ? <SupportPage key={workspaceGeneration} reference={window.location.pathname.split('/')[2]} /> : <PageNavigation><LadderShell /></PageNavigation>}
        </SupportBoundary>
        <SupportButton key={workspaceGeneration} />
      </NumberPopoverHost>

      <ExtensionConnect open={extensionConnect} onClose={() => setExtensionConnect(false)} />

      {isLoading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 9999 }}>
          <Box
            sx={{
              height: '100%',
              bgcolor: 'primary.main',
              animation: 'loading-bar 1.5s ease-in-out infinite',
              '@keyframes loading-bar': {
                '0%': { width: '0%', ml: '0%' },
                '50%': { width: '60%', ml: '20%' },
                '100%': { width: '0%', ml: '100%' },
              },
            }}
          />
        </Box>
      )}

      <Snackbar
        open={showSnackbar}
        autoHideDuration={10000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

function App() {
  return <AppThemeProvider>
    <CssBaseline />
    <AuthGate>
      <AppearancePreferenceSync />
      {window.location.pathname === '/design'
        ? <React.Suspense fallback={null}><DesignGallery /></React.Suspense>
        : <ProductExperience />}
    </AuthGate>
  </AppThemeProvider>;
}

export default App;
